import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import type { Importe } from '@actualgold/shared';
import { pedir } from '../api/cliente';
import type { TotalesDeVenta } from '@actualgold/shared';
import {
  useCondicionDeLaEmpresa,
  usePaqueteDeLaEmpresa,
  usePermiso,
} from '../sesion/ProveedorDeSesion';
import type { VarianteEncontrada } from './useVenta';

/**
 * Piezas que comparten las dos pantallas de venta.
 *
 * Lo que cambia entre el mostrador y la carga mayorista es **cómo entra la mercadería**: con el
 * lector y el dedo en uno, con el teclado en el otro. Lo que no cambia —qué se está vendiendo,
 * cuánto suma y cómo se cobra— vive acá, y así las dos pantallas no pueden decir cosas
 * distintas sobre la misma venta.
 */

/** Cuatro decimales son precisión interna; en el mostrador se mira con dos. */
export function conDosDecimales(importe: string): string {
  const [entera, fraccion = ''] = importe.split('.');
  return `${entera ?? '0'}.${fraccion.padEnd(2, '0').slice(0, 2)}`;
}

export function importeVisible(importe: Importe): string {
  return conDosDecimales(importe.aTexto());
}

export interface ClienteElegido {
  readonly id: string;
  readonly razonSocial: string;
  readonly condicionIva: string;
}

/**
 * Qué letra va a salir, dicha antes de cobrar.
 *
 * **No la elige nadie**: la calcula el mismo paquete de país que después usa el servidor
 * —en Argentina, con la RG 1415 citada al lado— así que la pantalla no puede prometer
 * una letra distinta de la que se emite. Que se vea de antemano es lo que evita el papelón de
 * descubrir la clase equivocada con el comprobante ya impreso.
 *
 * Si la empresa tiene una condición que no puede emitir, la función tira. Acá no se muestra
 * nada: el error de verdad aparece al intentar vender, con su texto, y no como una insignia rota
 * arriba del carrito. En los países sin letras tampoco se muestra nada.
 */
export function LetraDelComprobante({
  cliente,
}: {
  readonly cliente: ClienteElegido | null;
}): React.ReactElement | null {
  const { t } = useTranslation();
  const emisor = useCondicionDeLaEmpresa();
  const paquete = usePaqueteDeLaEmpresa();
  if (emisor === null || paquete === null) return null;

  let clase: string | null;
  try {
    clase = paquete.claseDelComprobante(
      emisor,
      cliente?.condicionIva ?? paquete.condicionDelReceptorPorOmision,
    );
  } catch {
    return null;
  }
  if (clase === null) return null;

  return (
    <span
      className="rounded-marco border border-borde px-2 py-0.5 text-xs font-semibold"
      title={t('venta.claseExplicacion')}
    >
      {t('venta.clase', { clase })}
    </span>
  );
}

/**
 * Cuánto debe ya el cliente que está enfrente.
 *
 * Se muestra al lado de su nombre porque es el dato que decide si conviene fiarle otra vez, y
 * quien atiende no debería tener que irse a otra pantalla a buscarlo con el cliente esperando.
 *
 * Si no tiene permiso para ver cuentas, no se pide: la consulta devolvería 403 y el error
 * aparecería en la consola sin que nadie hubiera pedido nada.
 */
function CuentaDelCliente({
  terceroId,
}: {
  readonly terceroId: string;
}): React.ReactElement | null {
  const { t } = useTranslation();
  const puedeVer = usePermiso('CUENTAS.CUENTA.VER');

  const consulta = useQuery({
    queryKey: ['cuenta-del-cliente', terceroId],
    enabled: puedeVer,
    queryFn: () => pedir<{ saldo: string }>(`/cuentas-corrientes/${terceroId}`),
  });

  if (!puedeVer || consulta.data === undefined) return null;

  const debe = Number(consulta.data.saldo) > 0;
  return (
    <span className="block text-xs text-texto-tenue">
      {debe
        ? t('venta.debeEnCuenta', { importe: conDosDecimales(consulta.data.saldo) })
        : t('venta.sinDeuda')}
    </span>
  );
}

/**
 * A quién se le vende.
 *
 * Arranca en "consumidor final" y **no es un campo obligatorio**: en un mostrador minorista la
 * enorme mayoría de las ventas son a alguien que no da datos, y pedir identificación para poder
 * cobrar sería inutilizar la pantalla más usada del sistema.
 *
 * No toma el foco al entrar. El foco es del buscador de artículos, porque el lector de códigos
 * escribe donde esté el cursor: si empezara acá, el primer código leído terminaría dentro de la
 * búsqueda de clientes.
 */
export function SelectorDeCliente({
  cliente,
  onElegir,
}: {
  readonly cliente: ClienteElegido | null;
  readonly onElegir: (cliente: ClienteElegido | null) => void;
}): React.ReactElement {
  const { t } = useTranslation();
  const [texto, setTexto] = useState('');

  const consulta = useQuery({
    queryKey: ['clientes-para-vender', texto],
    enabled: texto.trim().length >= 2,
    queryFn: () =>
      pedir<ClienteElegido[]>(
        `/terceros?soloClientes=true&texto=${encodeURIComponent(texto.trim())}`,
      ),
  });

  const resultados = consulta.data ?? [];

  if (cliente !== null) {
    return (
      <div className="flex items-center justify-between gap-2 rounded-marco border border-borde px-3 py-2">
        <span className="text-sm">
          <span className="flex items-center gap-2 text-xs text-texto-tenue">
            {t('venta.cliente')}
            <LetraDelComprobante cliente={cliente} />
          </span>
          {cliente.razonSocial}
          <CuentaDelCliente terceroId={cliente.id} />
        </span>
        <button
          type="button"
          className="rounded-marco border border-borde px-2 py-1 text-xs hover:bg-acento-suave"
          onClick={() => {
            onElegir(null);
            setTexto('');
          }}
        >
          {t('venta.quitarCliente')}
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <span className="flex items-center gap-2">
        <label className="text-xs font-medium text-texto-tenue" htmlFor="buscador-de-cliente">
          {t('venta.cliente')}
        </label>
        <LetraDelComprobante cliente={null} />
      </span>
      <input
        id="buscador-de-cliente"
        value={texto}
        autoComplete="off"
        placeholder={t('venta.consumidorFinal')}
        aria-label={t('venta.buscarCliente')}
        className="rounded-marco border border-borde bg-superficie px-3 py-2 text-sm"
        onChange={(evento) => {
          setTexto(evento.target.value);
        }}
        onKeyDown={(evento) => {
          if (evento.key === 'Escape') setTexto('');
        }}
      />

      {texto.trim().length >= 2 && resultados.length === 0 && !consulta.isPending ? (
        <p className="text-xs text-texto-tenue">{t('venta.sinClientesQueCoincidan')}</p>
      ) : null}

      {resultados.length > 0 ? (
        <ul className="flex flex-col gap-1">
          {resultados.slice(0, 6).map((encontrado) => (
            <li key={encontrado.id}>
              <button
                type="button"
                className="w-full rounded-marco border border-borde px-3 py-2 text-left text-sm hover:bg-acento-suave"
                onClick={() => {
                  onElegir(encontrado);
                  setTexto('');
                }}
              >
                {encontrado.razonSocial}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/**
 * Buscador con foco permanente.
 *
 * El foco vuelve solo después de cada agregado porque un lector de código de barras escribe
 * donde esté el cursor: si el foco se fue a otro lado, el código aparece en medio de un campo
 * de cantidad y la venta se ensucia sin que nadie entienda por qué.
 */
export function BuscadorDeArticulos({
  listaDePreciosId,
  onElegir,
  etiqueta,
  autoFoco = true,
}: {
  readonly listaDePreciosId: string | undefined;
  readonly onElegir: (variante: VarianteEncontrada) => void;
  readonly etiqueta: string;
  readonly autoFoco?: boolean;
}): React.ReactElement {
  const { t } = useTranslation();
  const [texto, setTexto] = useState('');
  const campo = useRef<HTMLInputElement>(null);

  const consulta = useQuery({
    queryKey: ['buscar-para-vender', texto, listaDePreciosId],
    enabled: texto.trim().length >= 2,
    queryFn: () =>
      pedir<VarianteEncontrada[]>(
        `/articulos/buscar?texto=${encodeURIComponent(texto.trim())}${
          listaDePreciosId === undefined ? '' : `&listaDePreciosId=${listaDePreciosId}`
        }`,
      ),
  });

  const resultados = consulta.data ?? [];

  useEffect(() => {
    if (autoFoco) campo.current?.focus();
  }, [autoFoco]);

  function elegir(variante: VarianteEncontrada): void {
    onElegir(variante);
    setTexto('');
    campo.current?.focus();
  }

  return (
    <div className="flex flex-col gap-2">
      <label className="text-xs font-medium text-texto-tenue" htmlFor="buscador-de-venta">
        {etiqueta}
      </label>
      <input
        id="buscador-de-venta"
        ref={campo}
        value={texto}
        autoComplete="off"
        className="rounded-marco border border-borde bg-superficie px-3 py-2 text-base"
        onChange={(evento) => {
          setTexto(evento.target.value);
        }}
        onKeyDown={(evento) => {
          if (evento.key === 'Escape') {
            setTexto('');
            return;
          }
          // El lector termina con Enter. Si hay un solo resultado, entra sin preguntar: cuando
          // el código de barras es exacto, cualquier confirmación es un paso de más.
          if (evento.key === 'Enter' && resultados.length > 0) {
            evento.preventDefault();
            const primero = resultados[0];
            if (primero !== undefined) elegir(primero);
          }
        }}
      />

      {texto.trim().length >= 2 && resultados.length === 0 && !consulta.isPending ? (
        <p className="text-xs text-texto-tenue">{t('venta.sinResultados')}</p>
      ) : null}

      {resultados.length > 0 ? (
        <ul className="flex flex-col gap-1">
          {resultados.slice(0, 8).map((variante) => (
            <li key={variante.varianteId}>
              <button
                type="button"
                className="flex w-full items-center justify-between gap-3 rounded-marco border border-borde px-3 py-2 text-left text-sm hover:bg-acento-suave"
                onClick={() => {
                  elegir(variante);
                }}
              >
                <span>
                  <span className="numero text-xs text-texto-tenue">{variante.codigo}</span>
                  <span className="block">{variante.nombre}</span>
                </span>
                <span className="numero">
                  {variante.precio === null
                    ? t('venta.sinPrecio')
                    : conDosDecimales(variante.precio)}
                </span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/** El resumen de la venta: lo que suma, lo que se bonificó y lo que se cobra. */
export function ResumenDeVenta({
  totales,
}: {
  readonly totales: TotalesDeVenta;
}): React.ReactElement {
  const { t } = useTranslation();

  return (
    <dl className="flex flex-col gap-1 text-sm">
      <div className="flex justify-between">
        <dt className="text-texto-tenue">{t('venta.subtotal')}</dt>
        <dd className="numero">{importeVisible(totales.subtotal)}</dd>
      </div>
      {totales.descuentoTotal.esCero() ? null : (
        <div className="flex justify-between">
          <dt className="text-texto-tenue">{t('venta.descuento')}</dt>
          <dd className="numero text-bien">{importeVisible(totales.descuentoTotal.negado())}</dd>
        </div>
      )}
      {/* El redondeo se muestra siempre que exista. Un total que no es la suma de las líneas
          tiene que explicarse solo, o el cliente pregunta y la cajera no sabe qué contestar. */}
      {totales.redondeo.esCero() ? null : (
        <div className="flex justify-between">
          <dt className="text-texto-tenue">{t('venta.redondeo')}</dt>
          <dd className="numero">{importeVisible(totales.redondeo)}</dd>
        </div>
      )}
      <div className="mt-1 flex items-baseline justify-between border-t border-borde pt-2">
        <dt className="font-medium">{t('venta.total')}</dt>
        <dd className="numero text-3xl font-semibold">{importeVisible(totales.total)}</dd>
      </div>
    </dl>
  );
}

/** Aviso de que no hay turno abierto, con el camino para resolverlo. */
export function SinTurno(): React.ReactElement {
  const { t } = useTranslation();
  return (
    <div className="rounded-marco border border-atencion bg-superficie p-4">
      <p className="text-sm">{t('venta.sinTurno')}</p>
      <div className="mt-3">
        <Link
          to="/caja"
          className="inline-flex items-center rounded-marco border border-transparent bg-acento px-3 py-1.5 text-sm font-medium text-white hover:opacity-90"
        >
          {t('venta.irACaja')}
        </Link>
      </div>
    </div>
  );
}
